import { useState, useEffect, useRef } from 'react';

// Single digit roller - renders a column of 0-9 and scrolls to show current digit
function DigitRoller({ digit, height = 24 }) {
  const numericDigit = parseInt(digit, 10);

  return (
    <span
      className="inline-block relative overflow-hidden leading-none"
      style={{
        fontVariantNumeric: 'tabular-nums',
        height: `${height}px`,
        width: '0.6em',
      }}
    >
      <span
        className="inline-flex flex-col transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
        style={{ transform: `translateY(-${numericDigit * height}px)` }}
      >
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
          <span
            key={d}
            className="flex items-center justify-center leading-none"
            style={{ height: `${height}px` }}
          >
            {d}
          </span>
        ))}
      </span>
    </span>
  );
}

// Animated price display with rolling digits
export default function AnimatedPrice({ value, height = 24, className = '' }) {
  const [displayValue, setDisplayValue] = useState(value);
  const displayValueRef = useRef(value);

  // Keep ref in sync with state
  useEffect(() => {
    displayValueRef.current = displayValue;
  }, [displayValue]);

  // Smooth interpolation for the value
  useEffect(() => {
    const startValue = displayValueRef.current;
    const endValue = value;
    const duration = 300;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic for natural deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = startValue + (endValue - startValue) * eased;

      setDisplayValue(current);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [value]);

  // Format with consistent decimal places
  const formatted = displayValue.toFixed(2);
  const [intPart, decPart] = formatted.split('.');

  return (
    <span
      className={`inline-flex items-center ${className}`}
      style={{ fontVariantNumeric: 'tabular-nums', height: `${height}px` }}
    >
      <span className="leading-none">$</span>
      {intPart.split('').map((char, i) => (
        <DigitRoller key={`int-${i}`} digit={char} height={height} />
      ))}
      <span className="leading-none">.</span>
      {decPart.split('').map((char, i) => (
        <DigitRoller key={`dec-${i}`} digit={char} height={height} />
      ))}
    </span>
  );
}
